// @flow

import { useCallback, useEffect, useMemo, useState } from 'react';
import { type IncomingPayment } from '@tectonics/prs/incoming-payment';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type CustomReceiptsHandler, type CustomReceiptsFetchStatus, fetchCustomReceipts } from '@tectonics/prs/custom-receipts';

export function useCustomReceiptsHandler(
  incomingPayment: IncomingPayment,
  setIncomingPayment: (IncomingPayment) => void,
  userInputValues: UserInputValues,
): CustomReceiptsHandler {
  const [shouldFetchCustomReceipts, setShouldFetchCustomReceipts] = useState<boolean>(false);
  const [customReceiptsFetchStatus, setCustomReceiptsFetchStatus] = useState<CustomReceiptsFetchStatus>('not-fetched');

  const requestFetchCustomReceipts = useCallback(() => {
    setShouldFetchCustomReceipts(true);
  }, [setShouldFetchCustomReceipts]);

  useEffect(() => {
    (async () => {
      if(shouldFetchCustomReceipts) {
        setShouldFetchCustomReceipts(false);

        try {
          setCustomReceiptsFetchStatus('fetching');
          await fetchCustomReceiptsAndUpdatePayment(incomingPayment, setIncomingPayment, userInputValues);
          setCustomReceiptsFetchStatus('ready');
        } catch(err) {
          setCustomReceiptsFetchStatus('failed');
        }
      }
    })();
  }, [incomingPayment, setIncomingPayment, setCustomReceiptsFetchStatus, shouldFetchCustomReceipts, setShouldFetchCustomReceipts, userInputValues]);

  return useMemo<CustomReceiptsHandler>(() => ({
    customReceiptsFetchStatus,
    requestFetchCustomReceipts,
  }), [customReceiptsFetchStatus, requestFetchCustomReceipts]);
}

async function fetchCustomReceiptsAndUpdatePayment(
  incomingPayment: IncomingPayment,
  setIncomingPayment: (IncomingPayment) => void,
  userInputValues: UserInputValues,
) {
  const checkout_receipts = await fetchCustomReceipts(incomingPayment, userInputValues);

  if(!checkout_receipts)
    return;

  const newIncomingPayment: IncomingPayment = {
    ...incomingPayment,
    checkout_receipts,
  };

  setIncomingPayment(newIncomingPayment);
}
